/**
 * DynamicFields - Field Configuration Management Page
 * 
 * Allows customers to create, edit, and delete dynamic field configurations
 * Light theme matching the overall Portal design
 */

import React, { useEffect, useState, useCallback, useMemo, useRef, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { toast } from 'react-toastify';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import ListIcon from '@mui/icons-material/List';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DynamicFieldsAPI from '../../Network/DynamicFields/DynamicFieldsAPI';
import '../../styles/ModernStyles.css';

// Animation keyframes
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Opacity-only animation (no transform) — safe for use with drag-and-drop ancestors
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Field type options
const FIELD_TYPES = [
    { value: 'text', label: 'Text', icon: <TextFieldsIcon /> },
    { value: 'number', label: 'Number', icon: <NumbersIcon /> },
    { value: 'select', label: 'Dropdown', icon: <ListIcon /> },
    { value: 'date', label: 'Date', icon: <CalendarTodayIcon /> },
    { value: 'checkbox', label: 'Checkbox', icon: <CheckBoxIcon /> },
];

// Field Type Icon Component
const FieldTypeIcon = ({ type }) => {
    const fieldType = FIELD_TYPES.find(f => f.value === type);
    return fieldType ? fieldType.icon : <TextFieldsIcon />;
};

// Empty state component
const EmptyState = ({ onAdd, t }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 6,
            background: '#FFFFFF',
            borderRadius: 3,
            border: '2px dashed #E2E8F0',
        }}
    >
        <Box
            sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(78, 205, 196, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 3,
            }}
        >
            <TextFieldsIcon sx={{ fontSize: 40, color: '#4ECDC4' }} />
        </Box>
        <Typography
            sx={{
                fontSize: 18,
                fontWeight: 600,
                color: '#1E293B',
                fontFamily: 'Cairo, sans-serif',
                marginBottom: 1,
            }}
        >
            {t('DYNAMIC_FIELDS.noFieldsTitle') || 'No Custom Fields Yet'}
        </Typography>
        <Typography
            sx={{
                fontSize: 14,
                color: '#64748B',
                fontFamily: 'Cairo, sans-serif',
                marginBottom: 3,
                textAlign: 'center',
            }}
        >
            {t('DYNAMIC_FIELDS.noFieldsDesc') || 'Create custom fields to collect additional data from your mobile app users'}
        </Typography>
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
            sx={{
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: 'Cairo, sans-serif',
                fontWeight: 600,
                padding: '10px 24px',
                '&:hover': {
                    background: 'linear-gradient(135deg, #44A08D 0%, #4ECDC4 100%)',
                },
            }}
        >
            {t('DYNAMIC_FIELDS.createFirstField') || 'Create Your First Field'}
        </Button>
    </Box>
);

export default function DynamicFields() {
    const [t] = useTranslation('common');
    const gridtheme = useSelector(state => state.themeData.gridtheme);
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [selectedField, setSelectedField] = useState(null);
    const [saving, setSaving] = useState(false);

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('all');
    const [searchRequired, setSearchRequired] = useState('all');

    const filteredFields = useMemo(() => {
        return fields.filter(field => {
            const matchesSearch = field.field_label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                field.field_name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesType = searchType === 'all' || field.field_type === searchType;
            const matchesRequired = searchRequired === 'all' ||
                (searchRequired === 'required' && field.is_required) ||
                (searchRequired === 'optional' && !field.is_required);
            return matchesSearch && matchesType && matchesRequired;
        });
    }, [fields, searchQuery, searchType, searchRequired]);

    // Actions menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuField, setMenuField] = useState(null);

    const handleMenuOpen = (event, field) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuField(field);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuField(null);
    };

    // Drag and drop handler
    const handleDragEnd = useCallback(async (result) => {
        if (!result.destination || result.source.index === result.destination.index) return;

        const reordered = Array.from(fields);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        // Optimistic update
        const previousFields = fields;
        setFields(reordered);

        // Build payload
        const updates = reordered.map((f, idx) => ({ field_id: f.field_id, display_order: idx }));

        try {
            const response = await DynamicFieldsAPI.reorderFields(updates);
            if (!response.status || !response.data?.status) {
                throw new Error('Reorder failed');
            }
        } catch (err) {
            console.error('Reorder error:', err);
            setFields(previousFields);
            toast.error(t('DYNAMIC_FIELDS.reorderError') || 'Failed to reorder fields');
        }
    }, [fields, t]);

    // Form state
    const [formData, setFormData] = useState({
        field_name: '',
        field_label: '',
        field_type: 'text',
        field_options: [],
        is_required: false,
        display_order: 0,
    });
    const [optionsText, setOptionsText] = useState('');

    // Fetch fields
    const fetchFields = useCallback(async () => {
        try {
            setLoading(true);
            const response = await DynamicFieldsAPI.getFieldConfig();
            if (response.status && response.data?.status && Array.isArray(response.data.data)) {
                setFields(response.data.data);
            } else {
                // Handle error response or invalid data
                console.warn('Invalid response from API:', response.data);
                setFields([]);
            }
        } catch (error) {
            console.error('Error fetching fields:', error);
            toast.error(t('DYNAMIC_FIELDS.fetchError') || 'Failed to load fields');
            setFields([]);
        } finally {
            setLoading(false);
        }
    }, [t]);

    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchFields();
    }, [fetchFields]);

    // Open dialog for new field
    const handleAddField = () => {
        // Generate a unique random field name
        let randomName = '';
        let isUnique = false;
        while (!isUnique) {
            const potentialName = 'field_' + Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
            if (!fields.some(f => f.field_name === potentialName)) {
                randomName = potentialName;
                isUnique = true;
            }
        }

        setSelectedField(null);
        setFormData({
            field_name: randomName,
            field_label: '',
            field_type: 'text',
            field_options: [],
            is_required: false,
            display_order: fields.length,
        });
        setOptionsText('');
        setDialogOpen(true);
    };

    // Open dialog for edit
    const handleEditField = (field) => {
        setSelectedField(field);
        setFormData({
            field_id: field.field_id,
            field_name: field.field_name,
            field_label: field.field_label,
            field_type: field.field_type,
            field_options: field.field_options || [],
            is_required: field.is_required === 1,
            display_order: field.display_order,
        });
        setOptionsText(field.field_options ? field.field_options.join('\n') : '');
        setDialogOpen(true);
    };

    // Open delete confirmation
    const handleDeleteClick = (field) => {
        setSelectedField(field);
        setDeleteDialogOpen(true);
    };

    // Save field
    const handleSaveField = async () => {
        try {
            setSaving(true);

            // Validate
            if (!formData.field_name.trim() || !formData.field_label.trim()) {
                toast.error(t('DYNAMIC_FIELDS.validationError') || 'Field name and label are required');
                return;
            }

            // Parse options for select type
            const dataToSave = {
                ...formData,
                field_name: formData.field_name.trim().toLowerCase(),
                field_options: formData.field_type === 'select'
                    ? optionsText.split('\n').filter(o => o.trim()).map(o => o.trim())
                    : null,
            };

            let response;
            if (selectedField) {
                response = await DynamicFieldsAPI.updateFieldConfig(dataToSave);
            } else {
                response = await DynamicFieldsAPI.createFieldConfig(dataToSave);
            }

            if (response.status && response.data?.status) {
                toast.success(selectedField
                    ? (t('DYNAMIC_FIELDS.updateSuccess') || 'Field updated successfully')
                    : (t('DYNAMIC_FIELDS.createSuccess') || 'Field created successfully')
                );
                setDialogOpen(false);
                fetchFields();
            } else {
                // Extract error message from various possible response formats
                let errorMessage = response.data?.data?.message
                    || response.data?.error?.message
                    || response.data?.message
                    || 'Operation failed';

                // Handle nested message objects (e.g., {message: {message: "..."}})
                if (errorMessage && typeof errorMessage === 'object') {
                    errorMessage = errorMessage.message || JSON.stringify(errorMessage);
                }

                console.error('Backend error:', response.data);
                toast.error(typeof errorMessage === 'string' ? errorMessage : 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving field:', error);
            toast.error(error.message || (t('DYNAMIC_FIELDS.saveError') || 'Failed to save field'));
        } finally {
            setSaving(false);
        }
    };

    // Delete field
    const handleDeleteField = async () => {
        try {
            setSaving(true);
            const response = await DynamicFieldsAPI.deleteFieldConfig(selectedField.field_id);

            if (response.status && response.data?.status) {
                toast.success(t('DYNAMIC_FIELDS.deleteSuccess') || 'Field deleted successfully');
                setDeleteDialogOpen(false);
                fetchFields();
            } else {
                // Extract error message from various possible response formats
                let errorMessage = response.data?.data?.message
                    || response.data?.error?.message
                    || response.data?.message
                    || 'Delete failed';

                // Handle nested message objects
                if (errorMessage && typeof errorMessage === 'object') {
                    errorMessage = errorMessage.message || JSON.stringify(errorMessage);
                }

                console.error('Delete error:', response.data);
                toast.error(typeof errorMessage === 'string' ? errorMessage : 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting field:', error);
            toast.error(error.message || (t('DYNAMIC_FIELDS.deleteError') || 'Failed to delete field'));
        } finally {
            setSaving(false);
        }
    };

    // Open restore confirmation
    const handleRestoreClick = (field) => {
        setSelectedField(field);
        setRestoreDialogOpen(true);
        handleMenuClose();
    };

    // Restore field
    const handleRestoreField = async () => {
        try {
            setSaving(true);
            const response = await DynamicFieldsAPI.restoreFieldConfig(selectedField.field_id);

            if (response.status && response.data?.status) {
                toast.success(t('DYNAMIC_FIELDS.restoreSuccess') || 'Field restored successfully');
                setRestoreDialogOpen(false);
                fetchFields();
            } else {
                let errorMessage = response.data?.data?.message
                    || response.data?.error?.message
                    || response.data?.message
                    || 'Restore failed';

                if (errorMessage && typeof errorMessage === 'object') {
                    errorMessage = errorMessage.message || JSON.stringify(errorMessage);
                }

                console.error('Restore error:', response.data);
                toast.error(typeof errorMessage === 'string' ? errorMessage : 'Restore failed');
            }
        } catch (error) {
            console.error('Error restoring field:', error);
            toast.error(error.message || (t('DYNAMIC_FIELDS.restoreError') || 'Failed to restore field'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 120px)',
                background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 100%)',
                borderRadius: 3,
                padding: 4,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                    animation: `${fadeInUp} 0.6s ease-out forwards`,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#1E293B',
                            fontFamily: 'Cairo, sans-serif',
                        }}
                    >
                        {t('DYNAMIC_FIELDS.title') || 'Dynamic Fields'}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 14,
                            color: '#64748B',
                            fontFamily: 'Cairo, sans-serif',
                        }}
                    >
                        {t('DYNAMIC_FIELDS.subtitle') || 'Configure custom fields for your mobile app forms'}
                    </Typography>
                </Box>
                {fields.length > 0 && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddField}
                        sx={{
                            background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontFamily: 'Cairo, sans-serif',
                            fontWeight: 600,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #44A08D 0%, #4ECDC4 100%)',
                            },
                        }}
                    >
                        {t('DYNAMIC_FIELDS.addField') || 'Add Field'}
                    </Button>
                )}
            </Box>

            {/* Search and Filters */}
            {!loading && fields.length > 0 && (
                <Box
                    sx={{
                        marginBottom: 3,
                        padding: 2,
                        background: '#FFFFFF',
                        borderRadius: 3,
                        border: '1px solid #E2E8F0',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                        animation: `${fadeInUp} 0.7s ease-out forwards`,
                    }}
                >
                    <TextField
                        size="small"
                        placeholder={t('DYNAMIC_FIELDS.searchPlaceholder') || 'Search field name or label...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94A3B8' }} />
                                </InputAdornment>
                            ),
                            sx: { fontFamily: 'Cairo, sans-serif', borderRadius: 2 }
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ fontFamily: 'Cairo, sans-serif' }}>{t('DYNAMIC_FIELDS.filterType') || 'Field Type'}</InputLabel>
                        <Select
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value)}
                            label={t('DYNAMIC_FIELDS.filterType') || 'Field Type'}
                            sx={{ fontFamily: 'Cairo, sans-serif', borderRadius: 2 }}
                        >
                            <MenuItem value="all" sx={{ fontFamily: 'Cairo, sans-serif' }}>
                                {t('DYNAMIC_FIELDS.allTypes') || 'All Types'}
                            </MenuItem>
                            {FIELD_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value} sx={{ fontFamily: 'Cairo, sans-serif' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {type.icon}
                                        <span>{type.label}</span>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ fontFamily: 'Cairo, sans-serif' }}>{t('DYNAMIC_FIELDS.filterMandatory') || 'Mandatory Status'}</InputLabel>
                        <Select
                            value={searchRequired}
                            onChange={(e) => setSearchRequired(e.target.value)}
                            label={t('DYNAMIC_FIELDS.filterMandatory') || 'Mandatory Status'}
                            sx={{ fontFamily: 'Cairo, sans-serif', borderRadius: 2 }}
                        >
                            <MenuItem value="all" sx={{ fontFamily: 'Cairo, sans-serif' }}>
                                {t('DYNAMIC_FIELDS.allStatus') || 'All'}
                            </MenuItem>
                            <MenuItem value="required" sx={{ fontFamily: 'Cairo, sans-serif' }}>
                                {t('DYNAMIC_FIELDS.statusRequired') || 'Required'}
                            </MenuItem>
                            <MenuItem value="optional" sx={{ fontFamily: 'Cairo, sans-serif' }}>
                                {t('DYNAMIC_FIELDS.statusOptional') || 'Optional'}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {/* Point Title Preview */}
            {!loading && fields.length > 0 && (() => {
                const titleParts = fields
                    .filter(f => f.is_required && f.field_type !== 'checkbox')
                    .map(f => f.field_label);
                const previewTitle = titleParts.length > 0 ? titleParts.join(' - ') : 'Point';
                return (
                    <Box
                        sx={{
                            marginBottom: 3,
                            padding: '14px 20px',
                            background: '#FFFFFF',
                            borderRadius: 3,
                            border: '1px solid #E2E8F0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            animation: `${fadeInUp} 0.6s ease-out forwards`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: 'rgba(78, 205, 196, 0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <TextFieldsIcon sx={{ fontSize: 18, color: '#4ECDC4' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: '#94A3B8',
                                    fontFamily: 'Cairo, sans-serif',
                                    lineHeight: 1.3,
                                }}
                            >
                                {t('DYNAMIC_FIELDS.pointTitlePreview') || 'Point Title Preview'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 15,
                                    fontWeight: 600,
                                    color: '#1E293B',
                                    fontFamily: 'Cairo, sans-serif',
                                    lineHeight: 1.4,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {previewTitle}
                            </Typography>
                        </Box>
                    </Box>
                );
            })()}

            {/* Content */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                    <CircularProgress sx={{ color: '#4ECDC4' }} />
                </Box>
            ) : fields.length === 0 ? (
                <EmptyState onAdd={handleAddField} t={t} />
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        borderRadius: 3,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    }}
                >
                    <Table sx={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '34%' }} />
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '10%' }} />
                        </colgroup>
                        <TableHead>
                            <TableRow sx={{ background: gridtheme?.primaryColor || '#707070' }}>
                                <TableCell sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: '#FFFFFF', borderRight: 'none', borderLeft: 'none', flex: 1 }}>
                                    {t('DYNAMIC_FIELDS.orderCol') || '#'}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: '#FFFFFF', borderRight: 'none', borderLeft: 'none', flex: 1 }}>
                                    {t('DYNAMIC_FIELDS.labelCol') || 'Label'}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: '#FFFFFF', borderRight: 'none', borderLeft: 'none', flex: 1 }}>
                                    {t('DYNAMIC_FIELDS.typeCol') || 'Type'}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: '#FFFFFF', borderRight: 'none', borderLeft: 'none', flex: 1 }}>
                                    {t('DYNAMIC_FIELDS.requiredCol') || 'Required'}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: '#FFFFFF', borderRight: 'none', borderLeft: 'none', flex: 0.4 }}>
                                    {t('DYNAMIC_FIELDS.actionsCol') || 'Actions'}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="fields-table">
                                {(provided) => (
                                    <TableBody
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {filteredFields.map((field, index) => {
                                            const isFiltering = searchQuery !== '' || searchType !== 'all' || searchRequired !== 'all';
                                            const isInactive = field.is_active === 0;
                                            const disableDrag = isInactive || isFiltering;

                                            return (
                                                <Draggable
                                                    key={String(field.field_id)}
                                                    draggableId={String(field.field_id)}
                                                    index={index}
                                                    isDragDisabled={disableDrag}
                                                >
                                                    {(provided, snapshot) => {
                                                        const row = (
                                                            <TableRow
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                sx={{
                                                                    '&:hover': { background: '#F8FAFC' },
                                                                    transition: 'all 0.2s',
                                                                    opacity: isInactive ? 0.5 : 1,
                                                                    ...(isInactive && {
                                                                        borderTop: '2px dashed #CBD5E1',
                                                                        borderBottom: '2px dashed #CBD5E1',
                                                                    }),
                                                                    ...(snapshot.isDragging && {
                                                                        background: '#FFFFFF',
                                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                                                        display: 'table',
                                                                        tableLayout: 'fixed',
                                                                        width: '100%',
                                                                        opacity: 1,
                                                                    }),
                                                                }}
                                                            >
                                                                <TableCell sx={{ borderRight: 'none', borderLeft: 'none', width: '8%' }}>
                                                                    <Box
                                                                        {...(disableDrag ? {} : provided.dragHandleProps)}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                            cursor: disableDrag ? 'not-allowed' : 'grab'
                                                                        }}
                                                                    >
                                                                        <DragIndicatorIcon sx={{ color: isInactive ? '#E2E8F0' : '#CBD5E1', fontSize: 18 }} />
                                                                        <Typography sx={{ fontFamily: 'Cairo, sans-serif', color: '#64748B' }}>
                                                                            {index + 1}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{ borderRight: 'none', borderLeft: 'none', width: '30%' }}>
                                                                    <Box>
                                                                        <Typography sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: '#1E293B', textDecoration: isInactive ? 'line-through' : 'none' }}>
                                                                            {field.field_label}
                                                                        </Typography>
                                                                        <Typography sx={{ fontFamily: 'Cairo, sans-serif', fontSize: 12, color: '#94A3B8' }}>
                                                                            {field.field_name}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell sx={{ borderRight: 'none', borderLeft: 'none', width: '22%' }}>
                                                                    <Chip
                                                                        icon={<FieldTypeIcon type={field.field_type} />}
                                                                        label={FIELD_TYPES.find(f => f.value === field.field_type)?.label || field.field_type}
                                                                        size="small"
                                                                        sx={{
                                                                            background: 'rgba(78, 205, 196, 0.1)',
                                                                            color: '#4ECDC4',
                                                                            fontFamily: 'Cairo, sans-serif',
                                                                            '& .MuiChip-icon': { color: '#4ECDC4' },
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ borderRight: 'none', borderLeft: 'none', width: '20%' }}>
                                                                    <Chip
                                                                        label={field.is_required ? (t('DYNAMIC_FIELDS.yes') || 'Yes') : (t('DYNAMIC_FIELDS.no') || 'No')}
                                                                        size="small"
                                                                        sx={{
                                                                            background: field.is_required ? 'rgba(239, 68, 68, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                                                                            color: field.is_required ? '#EF4444' : '#94A3B8',
                                                                            fontFamily: 'Cairo, sans-serif',
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ borderRight: 'none', borderLeft: 'none', width: '20%' }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => handleMenuOpen(e, field)}
                                                                        sx={{
                                                                            color: '#64748B',
                                                                            '&:hover': { background: 'rgba(100, 116, 139, 0.1)' },
                                                                        }}
                                                                    >
                                                                        <MoreVertIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                        // Portal the dragged row to document.body to avoid offset from ancestor CSS
                                                        if (snapshot.isDragging) {
                                                            return ReactDOM.createPortal(row, document.body);
                                                        }
                                                        return row;
                                                    }}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </TableBody>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Table>
                </TableContainer>
            )}

            {/* Actions Dropdown Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        minWidth: 160,
                    },
                }}
            >
                {menuField?.is_active === 0 ? (
                    <MenuItem
                        onClick={() => {
                            handleRestoreClick(menuField);
                            handleMenuClose();
                        }}
                        sx={{ fontFamily: 'Cairo, sans-serif', gap: 1 }}
                    >
                        <ListItemIcon sx={{ minWidth: '32px !important' }}>
                            <Box sx={{ color: '#4ECDC4' }}>
                                {/* You can use a RestoreIcon here if available, or just fallback text */}
                                <AddIcon fontSize="small" />
                            </Box>
                        </ListItemIcon>
                        <ListItemText
                            primary={t('GLOBAL.action_restore') || 'Restore'}
                            primaryTypographyProps={{ fontFamily: 'Cairo, sans-serif', fontSize: 14, color: '#4ECDC4' }}
                        />
                    </MenuItem>
                ) : (
                    <>
                        <MenuItem
                            onClick={() => {
                                handleEditField(menuField);
                                handleMenuClose();
                            }}
                            sx={{ fontFamily: 'Cairo, sans-serif', gap: 1 }}
                        >
                            <ListItemIcon sx={{ minWidth: '32px !important' }}>
                                <EditIcon fontSize="small" sx={{ color: '#6C9BCF' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('GLOBAL.action_edit') || 'Edit'}
                                primaryTypographyProps={{ fontFamily: 'Cairo, sans-serif', fontSize: 14 }}
                            />
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleDeleteClick(menuField);
                                handleMenuClose();
                            }}
                            sx={{ fontFamily: 'Cairo, sans-serif', gap: 1 }}
                        >
                            <ListItemIcon sx={{ minWidth: '32px !important' }}>
                                <DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('GLOBAL.action_delete') || 'Delete'}
                                primaryTypographyProps={{ fontFamily: 'Cairo, sans-serif', fontSize: 14, color: '#EF4444' }}
                            />
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Add/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                    {selectedField
                        ? (t('DYNAMIC_FIELDS.editField') || 'Edit Field')
                        : (t('DYNAMIC_FIELDS.addNewField') || 'Add New Field')
                    }
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 2 }}>
                        <TextField
                            label={t('DYNAMIC_FIELDS.fieldLabel') || 'Field Label'}
                            value={formData.field_label}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, field_label: e.target.value }));
                            }}
                            fullWidth
                            required
                            helperText={t('DYNAMIC_FIELDS.labelHelp') || 'Display name shown to users'}
                        />

                        <FormControl fullWidth>
                            <InputLabel>{t('DYNAMIC_FIELDS.fieldType') || 'Field Type'}</InputLabel>
                            <Select
                                value={formData.field_type}
                                onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                                label={t('DYNAMIC_FIELDS.fieldType') || 'Field Type'}
                            >
                                {FIELD_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {type.icon}
                                            <span>{type.label}</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {formData.field_type === 'select' && (
                            <TextField
                                label={t('DYNAMIC_FIELDS.options') || 'Options (one per line)'}
                                value={optionsText}
                                onChange={(e) => setOptionsText(e.target.value)}
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Option 1&#10;Option 2&#10;Option 3"
                            />
                        )}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_required}
                                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                                    color="primary"
                                />
                            }
                            label={t('DYNAMIC_FIELDS.required') || 'Required Field'}
                        />
                        <TextField
                            label={t('DYNAMIC_FIELDS.displayOrder') || 'Display Order'}
                            type="number"
                            value={formData.display_order}
                            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                            fullWidth
                            helperText={t('DYNAMIC_FIELDS.orderHelp') || 'Lower numbers appear first'}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 3 }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        sx={{ fontFamily: 'Cairo, sans-serif', color: '#64748B' }}
                    >
                        {t('DYNAMIC_FIELDS.cancel') || 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleSaveField}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                            fontFamily: 'Cairo, sans-serif',
                            fontWeight: 600,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #44A08D 0%, #4ECDC4 100%)',
                            },
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : (t('DYNAMIC_FIELDS.save') || 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                    {t('DYNAMIC_FIELDS.deleteConfirmTitle') || 'Delete Field?'}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Cairo, sans-serif', color: '#64748B' }}>
                        {t('DYNAMIC_FIELDS.deleteConfirmMessage') || 'Are you sure you want to delete this field? This action cannot be undone.'}
                    </Typography>
                    {selectedField && (
                        <Box sx={{ marginTop: 2, padding: 2, background: '#F8FAFC', borderRadius: 2 }}>
                            <Typography sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                                {selectedField.field_label}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: 3 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ fontFamily: 'Cairo, sans-serif', color: '#64748B' }}
                    >
                        {t('DYNAMIC_FIELDS.cancel') || 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleDeleteField}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: '#EF4444',
                            fontFamily: 'Cairo, sans-serif',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#DC2626',
                            },
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : (t('DYNAMIC_FIELDS.delete') || 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Restore Confirmation Dialog */}
            <Dialog
                open={restoreDialogOpen}
                onClose={() => setRestoreDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                    {t('DYNAMIC_FIELDS.restoreConfirmTitle') || 'Restore Field?'}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Cairo, sans-serif', color: '#64748B' }}>
                        {t('DYNAMIC_FIELDS.restoreConfirmMessage') || 'Are you sure you want to restore this field? It will become active again in the mobile app.'}
                    </Typography>
                    {selectedField && (
                        <Box sx={{ marginTop: 2, padding: 2, background: '#F8FAFC', borderRadius: 2 }}>
                            <Typography sx={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
                                {selectedField.field_label}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: 3 }}>
                    <Button
                        onClick={() => setRestoreDialogOpen(false)}
                        sx={{ fontFamily: 'Cairo, sans-serif', color: '#64748B' }}
                    >
                        {t('DYNAMIC_FIELDS.cancel') || 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleRestoreField}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: '#4ECDC4',
                            fontFamily: 'Cairo, sans-serif',
                            fontWeight: 600,
                            '&:hover': { background: '#45b8b0' },
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : (t('GLOBAL.action_restore') || 'Restore')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
